import { useMemo, useRef, useState } from 'react';
import {
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { TeamMember } from '@tassomutuo-backoffice/types';

type TeamGroupMembersTableProps = {
  rowCount: number;
  isPending: boolean;
  paginationModel: GridPaginationModel;
  setPaginationModel: (paginationModel: GridPaginationModel) => void;
  users: TeamMember[];
  onRemoveMemberFromTeamGroup: (row: TeamMember) => void;
  disabled: boolean;
};

const TeamGroupMembersTable = ({
  rowCount,
  isPending,
  paginationModel,
  setPaginationModel,
  users,
  onRemoveMemberFromTeamGroup,
  disabled,
}: TeamGroupMembersTableProps) => {
  const [anchorElActionsMenu, setAnchorElActionsMenu] =
    useState<null | HTMLElement>(null);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const actionsMenuOpen = Boolean(anchorElActionsMenu);

  const handleActionMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    member: TeamMember
  ) => {
    setAnchorElActionsMenu(event.currentTarget);
    setSelectedMember(member);
  };

  const handleCloseActionsMenu = () => {
    setAnchorElActionsMenu(null);
    setSelectedMember(null);
  };

  const handleRemoveMemberFromTeam = () => {
    if (selectedMember) {
      onRemoveMemberFromTeamGroup(selectedMember);
      handleCloseActionsMenu();
    }
  };

  const rowCountRef = useRef(rowCount);

  const rowCountMemoized = useMemo(() => {
    if (rowCount !== undefined && rowCount !== 0) {
      rowCountRef.current = rowCount;
    }
    return rowCountRef.current;
  }, [rowCount]);

  const tableColumns: GridColDef<TeamMember>[] = [
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
    },
    {
      field: 'name',
      headerName: 'Nome',
      flex: 1,
    },

    {
      field: 'oldestLead',
      sortable: false,
      headerName: 'Lead non aggiornate',
      flex: 1,
      headerAlign: 'center',
      maxWidth: 150,
      renderCell: ({ row }) => {
        return (
          <Box display="flex" justifyContent="center" width="100%">
            <p>{row?.statistics?.oldestLead}</p>
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      minWidth: 150,
      renderCell: ({ row }) => {
        return (
          <Box display="flex" gap={1}>
            <IconButton
              id={`${row.id}-menu`}
              aria-controls={actionsMenuOpen ? 'basic-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={actionsMenuOpen ? 'true' : undefined}
              onClick={(event) => handleActionMenuClick(event, row)}
            >
              <MoreVertIcon />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  return (
    <div>
      <Paper sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', pt: 2, px: 2 }}>
          Membri del gruppo ({rowCountMemoized})
        </Typography>
        <Divider sx={{ m: 2 }} />
        <DataGrid
          columns={tableColumns}
          rows={users || []}
          disableRowSelectionOnClick
          autoHeight
          rowCount={rowCountMemoized}
          loading={isPending}
          pageSizeOptions={[10]}
          paginationModel={paginationModel}
          paginationMode="server"
          onPaginationModelChange={setPaginationModel}
        />
      </Paper>
      <Menu
        anchorEl={anchorElActionsMenu}
        open={actionsMenuOpen}
        onClose={handleCloseActionsMenu}
        elevation={1}
      >
        <MenuItem onClick={handleRemoveMemberFromTeam} disabled={disabled}>
          Rimuovi membro dal gruppo
        </MenuItem>
      </Menu>
    </div>
  );
};

export default TeamGroupMembersTable;
